import * as SubframeCore from '@subframe/core';
import { useQuery } from '@tanstack/react-query';
import { ORDER_OPTIONS, RANK_BY_OPTIONS } from 'modules/Recruit/constants/filters';
import { getLocationsByRegion } from 'modules/Recruit/services/recruit';
import { CandidateListFilter } from 'modules/Recruit/types';
import { ApiResponse, LocationsByRegionData } from 'modules/Recruit/types/api';
import React from 'react';
import { Alert } from 'subframe/components/Alert';
import { Select } from 'subframe/components/Select';
import { TabsPills } from 'subframe/components/TabsPills';
import { TextField } from 'subframe/components/TextField';
import endpoints from '../../../constants/endpoints';

interface CandidateListFilterProps {
  filters: CandidateListFilter;
  onFilterChange: (filters: Partial<CandidateListFilter>) => void;
}

export const CandidateListFilters: React.FC<CandidateListFilterProps> = ({
  filters,
  onFilterChange,
}) => {
  const {
    data: locationsByRegion,
    isLoading: isLoadingLocations,
    error: locationsError,
    isError: isLocationsError,
  } = useQuery({
    queryKey: [endpoints.locationsByRegion],
    queryFn: getLocationsByRegion,
    select: (res: ApiResponse<LocationsByRegionData>) => res?.result?.regions,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  const availableRegions = locationsByRegion ? Object.keys(locationsByRegion) : [];
  const availableLocations = locationsByRegion?.[filters.region] ?? [];

  const handleFilterChange = <K extends keyof CandidateListFilter>(
    key: K,
    value: CandidateListFilter[K]
  ) => {
    onFilterChange({ [key]: value });
  };

  const handleRegionChange = (value: string) => {
    onFilterChange({ region: value, location: 'all' });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ search: e.target.value });
  };

  if (isLocationsError) {
    return (
      <div className="w-full">
        <Alert variant="error" title="Error loading locations">
          {locationsError?.message ||
            'Failed to load location data. Some filters may not work correctly.'}
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Search Bar */}
      <div className="flex w-full items-start gap-2 mb-6">
        <TextField
          className="h-auto grow shrink-0 basis-0"
          label=""
          helpText=""
          icon="FeatherSearch"
        >
          <TextField.Input
            placeholder="Search by candidate's name"
            value={filters.search || ''}
            onChange={handleSearchChange}
            className="w-full max-w-md"
          />
        </TextField>
      </div>

      <div className="flex w-full flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-body text-default-font">Show</span>
          <SubframeCore.Icon
            className="text-body font-body text-default-font"
            name="FeatherListFilter"
          />
        </div>
        <Select
          className="h-auto w-auto flex-none self-stretch"
          label=""
          placeholder="All Regions"
          helpText=""
          value={filters.region}
          onValueChange={handleRegionChange}
          disabled={isLoadingLocations}
        >
          <Select.Item value="all">All Regions</Select.Item>
          {availableRegions?.map((region: string) => (
            <Select.Item key={region} value={region}>
              {region}
            </Select.Item>
          ))}
        </Select>
        <Select
          placeholder="All Locations"
          value={filters.location}
          onValueChange={value => handleFilterChange('location', value as string)}
          disabled={
            isLoadingLocations ||
            !filters.region ||
            filters.region === 'all' ||
            availableLocations.length === 0
          }
        >
          <Select.Item value="all">All Locations</Select.Item>
          {availableLocations.map((location: string) => (
            <Select.Item key={location} value={location}>
              {location}
            </Select.Item>
          ))}
        </Select>
        {/* <Select
          placeholder="Stage"
          value={filters.stage}
          onValueChange={(value) => handleFilterChange("stage", value as string)}
        >
          {PIPELINE_STAGE_OPTIONS.map((opt) => (
            <Select.Item key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Item>
          ))}
        </Select> */}
        <div className="h-6 w-px bg-neutral-border" />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-body text-default-font">Rank by</span>
          <SubframeCore.Icon
            className="text-body font-body text-default-font"
            name="FeatherArrowUpDown"
          />
          <Select
            placeholder="Date Applied"
            value={filters.rankBy}
            onValueChange={value => handleFilterChange('rankBy', value as string)}
          >
            {RANK_BY_OPTIONS.map(opt => (
              <Select.Item key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Item>
            ))}
          </Select>

          <Select
            placeholder="Order"
            value={filters.order}
            onValueChange={value => handleFilterChange('order', value as 'asc' | 'desc')}
          >
            {ORDER_OPTIONS.map(opt => (
              <Select.Item key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Item>
            ))}
          </Select>
        </div>
        <TabsPills className="h-auto w-auto flex-none" />
      </div>
    </div>
  );
};
